
import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Collapse } from "reactstrap";

// Import Data
import navdata from "../LayoutMenuData";

// i18n
import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";

import { useSelector } from "react-redux";
import { createSelector } from "reselect";

const VerticalLayout = (props) => {
    const navData = navdata().props.children;
    const path = props.router.location.pathname;

    /*
    =====================================================
    LAYOUT SETTINGS
    =====================================================
    */

    const selectLayoutState = (state) => state.Layout;

    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            leftsidbarSizeType: layout.leftsidbarSizeType,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
            layoutType: layout.layoutType,
        })
    );

    const {
        leftsidbarSizeType,
        sidebarVisibilitytype,
        layoutType,
    } = useSelector(selectLayoutProperties);

    /*
    =====================================================
    SIDEBAR RESIZE
    =====================================================
    */

    const resizeSidebarMenu = useCallback(() => {
        var windowSize = document.documentElement.clientWidth;
        var hamburgerIcon = document.querySelector(".hamburger-icon");

        if (windowSize >= 1025) {

            if (
                document.documentElement.getAttribute("data-layout") ===
                "vertical"
            ) {
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    leftsidbarSizeType
                );
            }

            if (
                document.documentElement.getAttribute("data-layout") ===
                "semibox"
            ) {
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    leftsidbarSizeType
                );
            }

            if (
                (sidebarVisibilitytype === "show" ||
                    layoutType === "vertical" ||
                    layoutType === "twocolumn") &&
                document.querySelector(".hamburger-icon")
            ) {
                if (hamburgerIcon !== null) {
                    hamburgerIcon.classList.remove("open");
                }
            } else {
                if (hamburgerIcon !== null) {
                    hamburgerIcon.classList.add("open");
                }
            }

        } else if (windowSize < 1025 && windowSize > 767) {

            document.body.classList.remove("twocolumn-panel");

            if (
                document.documentElement.getAttribute("data-layout") ===
                "vertical"
            ) {
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    "sm"
                );
            }

            if (
                document.documentElement.getAttribute("data-layout") ===
                "semibox"
            ) {
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    "sm"
                );
            }

            if (document.querySelector(".hamburger-icon")) {
                document
                    .querySelector(".hamburger-icon")
                    .classList.add("open");
            }

        } else if (windowSize <= 767) {

            document.body.classList.remove("vertical-sidebar-enable");

            if (
                document.documentElement.getAttribute("data-layout") !==
                "horizontal"
            ) {
                document.documentElement.setAttribute(
                    "data-sidebar-size",
                    "lg"
                );
            }

            if (document.querySelector(".hamburger-icon")) {
                document
                    .querySelector(".hamburger-icon")
                    .classList.add("open");
            }
        }
    }, [
        leftsidbarSizeType,
        sidebarVisibilitytype,
        layoutType,
    ]);

    useEffect(() => {
        window.addEventListener(
            "resize",
            resizeSidebarMenu,
            true
        );

        return () => {
            window.removeEventListener(
                "resize",
                resizeSidebarMenu,
                true
            );
        };
    }, [resizeSidebarMenu]);

    /*
    =====================================================
    ACTIVE MENU
    =====================================================
    */

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

        const initMenu = () => {
            const pathName =
                process.env.PUBLIC_URL + path;

            const ul =
                document.getElementById("navbar-nav");

            if (!ul) return;

            const items =
                ul.getElementsByTagName("a");

            let itemsArray = [...items];

            removeActivation(itemsArray);

            let matchingMenuItem =
                itemsArray.find((x) => {
                    return x.pathname === pathName;
                });

            if (matchingMenuItem) {
                activateParentDropdown(
                    matchingMenuItem
                );
            }
        };

        initMenu();
    }, [path, props.layoutType]);

    function activateParentDropdown(item) {

        item.classList.add("active");

        let parentCollapseDiv =
            item.closest(".collapse.menu-dropdown");

        if (parentCollapseDiv) {

            parentCollapseDiv.classList.add("show");

            if (parentCollapseDiv.parentElement) {

                parentCollapseDiv.parentElement
                    .children[0]
                    .classList.add("active");

                parentCollapseDiv.parentElement
                    .children[0]
                    .setAttribute(
                        "aria-expanded",
                        "true"
                    );
            }

            /*
            =================================================
            SUPPORT MULTIPLE NESTED LEVELS
            =================================================
            */

            let parent =
                parentCollapseDiv.parentElement;

            while (parent) {

                const parentCollapse =
                    parent.closest(
                        ".collapse.menu-dropdown"
                    );

                if (!parentCollapse) break;

                parentCollapse.classList.add("show");

                const previous =
                    parentCollapse.previousElementSibling;

                if (previous) {
                    previous.classList.add("active");
                    previous.setAttribute(
                        "aria-expanded",
                        "true"
                    );
                }

                parent =
                    parentCollapse.parentElement;
            }

            return false;
        }

        return false;
    }

    const removeActivation = (items) => {

        let actiItems = items.filter((x) =>
            x.classList.contains("active")
        );

        actiItems.forEach((item) => {

            if (item.classList.contains("menu-link")) {

                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove(
                        "show"
                    );
                }

                item.setAttribute(
                    "aria-expanded",
                    false
                );
            }

            if (item.classList.contains("nav-link")) {

                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove(
                        "show"
                    );
                }

                item.setAttribute(
                    "aria-expanded",
                    false
                );
            }

            item.classList.remove("active");
        });
    };

    /*
    =====================================================
    RECURSIVE MENU RENDERER
    =====================================================

    Supports:

    PMS
      ↓
    Dashboard
      ↓
    Dashboard Analytics

    PMS
      ↓
    Orders & Billing
      ↓
    Create Order

    PMS
      ↓
    Housekeeping
      ↓
    Dashboard
    */

    const renderMenuItems = (
        items,
        level = 0
    ) => {

        if (!items || !items.length) {
            return null;
        }

        return items.map((item, key) => {

            /*
            =================================================
            ITEM HAS CHILDREN
            =================================================
            */

            if (
                item.subItems &&
                item.subItems.length > 0
            ) {

               return (
  <li
    className="nav-item"
    key={item.id || key}
  >
    <Link
      onClick={item.click ? item.click : undefined}
      className="nav-link menu-link"
      to={item.link ? item.link : "/#"}
    >
      {item.icon && (
        <i className={item.icon}></i>
      )}

      <span>
        {props.t(item.label)}
      </span>

      {item.badgeName ? (
        <span
          className={
            "badge badge-pill bg-" +
            item.badgeColor
          }
        >
          {item.badgeName}
        </span>
      ) : null}
    </Link>
  </li>
);
            }

            /*
            =================================================
            NORMAL LINK
            =================================================
            */

            return (
                <li
                    className="nav-item"
                    key={
                        item.id ||
                        `${level}-${key}`
                    }
                >

                    <Link
                        to={
                            item.link
                                ? item.link
                                : "/#"
                        }
                        className="nav-link"
                    >

                        {props.t(
                            item.label
                        )}

                        {item.badgeName ? (
                            <span
                                className={
                                    "badge badge-pill bg-" +
                                    item.badgeColor
                                }
                            >
                                {item.badgeName}
                            </span>
                        ) : null}

                    </Link>

                </li>
            );
        });
    };

    /*
    =====================================================
    RENDER
    =====================================================
    */

    return (
        <React.Fragment>

            {(navData || []).map(
                (item, key) => {

                    /*
                    =============================================
                    HEADER
                    =============================================
                    */

                    if (item.isHeader) {

                        return (
                            <li
                                className="menu-title"
                                key={key}
                            >
                                <span>
                                    {props.t(
                                        item.label
                                    )}
                                </span>
                            </li>
                        );
                    }

                    /*
                    =============================================
                    MAIN MENU WITH CHILDREN
                    =============================================
                    */

                    if (
                        item.subItems &&
                        item.subItems.length > 0
                    ) {

                        return (
                            <li
                                className="nav-item"
                                key={
                                    item.id ||
                                    key
                                }
                            >

                                <Link
                                    onClick={
                                        item.click
                                            ? item.click
                                            : undefined
                                    }
                                    className="nav-link menu-link"
                                    to={
                                        item.link
                                            ? item.link
                                            : "/#"
                                    }
                                    data-bs-toggle="collapse"
                                >

                                    {item.icon && (
                                        <i
                                            className={
                                                item.icon
                                            }
                                        ></i>
                                    )}

                                    <span>
                                        {props.t(
                                            item.label
                                        )}
                                    </span>

                                    {item.badgeName ? (
                                        <span
                                            className={
                                                "badge badge-pill bg-" +
                                                item.badgeColor
                                            }
                                        >
                                            {
                                                item.badgeName
                                            }
                                        </span>
                                    ) : null}

                                </Link>

                                <Collapse
                                    className="menu-dropdown"
                                    isOpen={
                                        item.stateVariables
                                    }
                                >

                                    <ul className="nav nav-sm flex-column">

                                        {renderMenuItems(
                                            item.subItems,
                                            1
                                        )}

                                    </ul>

                                </Collapse>

                            </li>
                        );
                    }

                    /*
                    =============================================
                    NORMAL MAIN LINK
                    =============================================
                    */

                    return (
                        <li
                            className="nav-item"
                            key={
                                item.id ||
                                key
                            }
                        >

                            <Link
                                className="nav-link menu-link"
                                to={
                                    item.link
                                        ? item.link
                                        : "/#"
                                }
                            >

                                {item.icon && (
                                    <i
                                        className={
                                            item.icon
                                        }
                                    ></i>
                                )}

                                <span>
                                    {props.t(
                                        item.label
                                    )}
                                </span>

                                {item.badgeName ? (
                                    <span
                                        className={
                                            "badge badge-pill bg-" +
                                            item.badgeColor
                                        }
                                    >
                                        {
                                            item.badgeName
                                        }
                                    </span>
                                ) : null}

                            </Link>

                        </li>
                    );
                }
            )}

        </React.Fragment>
    );
};

VerticalLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withRouter(
    withTranslation()(VerticalLayout)
);